import {
  APILibraryCategory,
  type APILibraryCategoryResponseType,
} from "./APILibraryCategory";
import {
  APILibraryVertical,
  type APILibraryVerticalResponseType,
} from "./APILibraryVertical";

export type APILibraryTemplateResponseType = Partial<{
  id: number;
  url: string;
  name: string;
  type: "form" | "media" | "model";
  verticals: APILibraryVerticalResponseType[];
  categories: APILibraryCategoryResponseType[];
  source: string;
  status: "draft" | "published" | "archived";
  version: number;
  description: string;
  fields_count: number;
  shots_count: number | null;
  recommended: boolean;
  created: string;
  modified: string;
  payload_json: Record<string, any>;
  packs: string[];
  submitted_by: string;
  submitted_from: string;
}>;
export type APILibraryTemplateRequestType = {
  type: "form" | "media" | "model";
  json: Record<string, any>;
};
export type APILibraryTemplateMetadataRequestType = Partial<
  Pick<
    Required<APILibraryTemplateResponseType>,
    | "name"
    | "type"
    | "verticals"
    | "categories"
    | "description"
    | "recommended"
    | "packs"
  >
>;
export interface IAPILibraryTemplate {
  getData(): APILibraryTemplateResponseType;
  verticals(): APILibraryVertical[];
  categories(): APILibraryCategory[];
  isDataValid(): boolean;
  isDataValidModel(): boolean;
  getModelValidationErrors(): string[];
  toRequestModel(): APILibraryTemplateRequestType;
}
export class APILibraryTemplate implements IAPILibraryTemplate {
  readonly data: APILibraryTemplateResponseType;
  constructor(data: APILibraryTemplateResponseType) {
    this.data = data;
  }
  getData(): APILibraryTemplateResponseType {
    return this.data;
  }
  isDataValid(): boolean {
    return !!this.data.name && !!this.data.type && !!this.data.payload_json;
  }
  isDataValidModel(): boolean {
    return this.getModelValidationErrors().length === 0;
  }
  getModelValidationErrors(): string[] {
    const errors: string[] = [];
    const payload = this.data.payload_json;

    if (!this.data.type) {
      errors.push("Template type is required.");
    }
    if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
      errors.push("JSON definition must be an object.");
      return errors;
    }
    if (!isNonEmptyString(payload.name)) {
      errors.push("json.name is required.");
    }

    if (this.data.type === "form") {
      const sectionsData = payload.sectionsData;
      const questions = payload.questions;
      if (!isNonEmptyArray(sectionsData) && !isNonEmptyArray(questions)) {
        errors.push("json.sectionsData or json.questions is required.");
      }
    }

    if (this.data.type === "media") {
      const shots = payload.shots;
      if (!isNonEmptyArray(shots)) {
        errors.push("json.shots must be a non-empty array.");
      } else if (!shots.every(isPlainObject)) {
        errors.push("Every shot must be an object.");
      }
    }

    if (this.data.type === "model") {
      const keyFields = payload.keyFields;
      if (!isNonEmptyArray(keyFields)) {
        errors.push("json.keyFields must be a non-empty array.");
      } else if (
        !keyFields.every(
          (field) =>
            isPlainObject(field) &&
            isNonEmptyString(field.external_id) &&
            isNonEmptyString(field.label),
        )
      ) {
        errors.push("Every key field must include external_id and label.");
      }
    }

    return errors;
  }
  toRequestModel(): APILibraryTemplateRequestType {
    if (this.isDataValidModel()) {
      return { type: this.data.type!, json: this.data.payload_json! };
    }
    throw new Error(this.getModelValidationErrors().join(" "));
  }
  verticals(): APILibraryVertical[] {
    return this.data.verticals?.map((v) => new APILibraryVertical(v)) ?? [];
  }
  categories(): APILibraryCategory[] {
    return this.data.categories?.map((c) => new APILibraryCategory(c)) ?? [];
  }
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isNonEmptyArray(value: unknown): value is unknown[] {
  return Array.isArray(value) && value.length > 0;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}
